import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Grid, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip,
  FormControlLabel, Radio, Checkbox, IconButton, Alert
} from '@mui/material';
import {
  HelpOutline as QuestionsIcon, Add as AddIcon,
  ArrowBack as ArrowBackIcon, DeleteOutline as DeleteIcon, Edit as EditIcon
} from '@mui/icons-material';
import { academyApi } from '../services/academyApi';
import { academyStore } from '../services/academyStore';
import { Quiz, QuizQuestion, QuizQuestionOption, QuizQuestionType } from '../types/academy';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import GenericPage from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { useSearchParams, useNavigate } from 'react-router-dom';

const fsx = {
  '& .MuiInputBase-root': { fontSize: '0.85rem' },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
};

const emptyOptions = (): QuizQuestionOption[] => [
  { key: 'A', label: '', isCorrect: true },
  { key: 'B', label: '', isCorrect: false },
];

const emptyQuestion = (quizId: number): Partial<QuizQuestion> => ({
  quiz_id: quizId,
  text: '',
  type: 'single',
  options: emptyOptions(),
  order: 1,
  points: 1,
});

const nextKey = (count: number): string =>
  count < 26 ? String.fromCharCode(65 + count) : `K${count + 1}`;

export const QuizQuestionsPage: React.FC = () => {
  const { t } = useLanguage();
  
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuizId = Number(searchParams.get('qiz_id')) || 0;

  const [quizzes, setQuizzes] = useState<Quiz[]>(() => academyStore.getQuizzes());
  const [selectedQuizId, setSelectedQuizId] = useState<number>(initialQuizId);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<QuizQuestion>>(emptyQuestion(initialQuizId));
  const [confirmDelete, setConfirmDelete] = useState<QuizQuestion | null>(null);

  useEffect(() => {
    setQuizzes(academyStore.getQuizzes());
    return academyStore.subscribe(() => {
      setQuizzes(academyStore.getQuizzes());
    });
  }, []);

  useEffect(() => {
    setSelectedQuizId(Number(searchParams.get('qiz_id')) || 0);
  }, [searchParams]);

  const loadQuestions = useCallback(async (quizId: number) => {
    setLoading(true);
    try {
      const list = await academyApi.getQuizQuestions(quizId);
      setQuestions(list || []);
    } catch (e: any) {
      blobticMessage.error(t('academy.quiz_questions.error_load'), e.message);
    } finally {
      setLoading(false);
    }
  }, [blobticMessage.error, t]);

  useEffect(() => {
    if (selectedQuizId) loadQuestions(selectedQuizId);
    else setQuestions([]);
  }, [selectedQuizId, loadQuestions]);

  const handleQuizChange = (quizId: number) => {
    setSearchParams(quizId ? { qiz_id: String(quizId) } : {});
  };

  const openNew = () => {
    if (!selectedQuizId) return;
    setEditingId(null);
    setFormData(emptyQuestion(selectedQuizId));
    setDialogOpen(true);
  };

  const openEdit = (q: QuizQuestion) => {
    setEditingId(q.id);
    setFormData({
      ...q,
      options: (q.options || []).map(o => ({ ...o })),
    });
    setDialogOpen(true);
  };

  const handleTypeChange = (type: QuizQuestionType) => {
    setFormData(prev => {
      const prevType = prev.type;
      const needsReseed = prevType === 'boolean' || prevType === 'text' || !(prev.options || []).length;
      return {
        ...prev,
        type,
        options: type === 'text'
          ? []
          : type === 'boolean'
            ? [
                { key: 'true', label: t('academy.quiz_questions.form.true_label'), isCorrect: true },
                { key: 'false', label: t('academy.quiz_questions.form.false_label'), isCorrect: false },
              ]
            : needsReseed
              ? emptyOptions()
              : prev.options,
      };
    });
  };

  const markSingleCorrect = (key: string) => {
    setFormData(prev => ({
      ...prev,
      options: (prev.options || []).map(o => ({ ...o, isCorrect: o.key === key })),
    }));
  };

  const toggleMultiCorrect = (key: string) => {
    setFormData(prev => ({
      ...prev,
      options: (prev.options || []).map(o => (o.key === key ? { ...o, isCorrect: !o.isCorrect } : o)),
    }));
  };

  const handleOptionChange = (key: string, label: string) => {
    setFormData(prev => ({
      ...prev,
      options: (prev.options || []).map(o => (o.key === key ? { ...o, label } : o)),
    }));
  };

  const addOption = () => {
    setFormData(prev => {
      const list = prev.options || [];
      return {
        ...prev,
        options: [...list, { key: nextKey(list.length), label: '', isCorrect: false }],
      };
    });
  };

  const removeOption = (key: string) => {
    setFormData(prev => {
      const list = (prev.options || []).filter(o => o.key !== key);
      const reKeyed = list.map((o, i) => ({ ...o, key: nextKey(i) }));
      if (reKeyed.length && !reKeyed.some(o => o.isCorrect)) {
        reKeyed[0] = { ...reKeyed[0], isCorrect: true };
      }
      return { ...prev, options: reKeyed };
    });
  };

  const handleSave = async () => {
    if (!selectedQuizId) return;
    if (!formData.text?.trim()) {
      blobticMessage.error(t('academy.quiz_questions.error_save'), t('academy.quiz_questions.val_text'));
      return;
    }
    const options = formData.options || [];
    const isText = formData.type === 'text';
    if (!isText) {
      if (options.length < 2) {
        blobticMessage.error(t('academy.quiz_questions.error_save'), t('academy.quiz_questions.val_options'));
        return;
      }
      if (options.some(o => !o.label.trim())) {
        blobticMessage.error(t('academy.quiz_questions.error_save'), t('academy.quiz_questions.val_options'));
        return;
      }
      if (!options.some(o => o.isCorrect)) {
        blobticMessage.error(t('academy.quiz_questions.error_save'), t('academy.quiz_questions.val_correct'));
        return;
      }
    }
    const payload = { ...formData, options, quiz_id: selectedQuizId };
    const res = editingId
      ? await academyApi.updateQuizQuestion(editingId, payload)
      : await academyApi.createQuizQuestion(payload);
    if (res?.success) {
      blobticMessage.success(t('common.messages.success'), t('common.messages.saved'));
      setDialogOpen(false);
      loadQuestions(selectedQuizId);
    } else {
      blobticMessage.error(t('academy.quiz_questions.error_save'), res?.message || '');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await academyApi.deleteQuizQuestion(confirmDelete.id);
      blobticMessage.success(t('common.messages.success'), t('common.messages.deleted_success'));
      setConfirmDelete(null);
      loadQuestions(selectedQuizId);
    } catch (e: any) {
      blobticMessage.error(t('academy.quiz_questions.error_delete'), e.message);
    }
  };

  const selectedQuiz = quizzes.find(q => q.id === selectedQuizId);

  const rows = questions.map(q => ({
    ...q,
    typeLabel: q.type,
    correctKeys: q.type === 'text'
      ? t('academy.quiz_questions.table.manual')
      : (q.options || []).filter(o => o.isCorrect).map(o => o.key).join(', ') || '—',
  }));

  const typeLabel = (type: QuizQuestionType) =>
    type === 'single' ? t('academy.quiz_questions.form.type_single')
      : type === 'multi' ? t('academy.quiz_questions.form.type_multi')
      : type === 'boolean' ? t('academy.quiz_questions.form.type_boolean')
      : t('academy.quiz_questions.form.type_text');

  const correctControl = (option: QuizQuestionOption) => {
    if (formData.type === 'multi') {
      return (
        <Checkbox
          size="small"
          color="success"
          checked={!!option.isCorrect}
          onChange={() => toggleMultiCorrect(option.key)}
        />
      );
    }
    return (
      <Radio
        size="small"
        color="success"
        name={`correct-${formData.text ?? 'q'}`}
        checked={!!option.isCorrect}
        onChange={() => markSingleCorrect(option.key)}
      />
    );
  };

  return (
    <GenericPage
      title={t('academy.quiz_questions.title')}
      icon={<QuestionsIcon />}
      footerEnabled={false}
      additionalActions={
        <>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/Academy/quizzes')} sx={{ borderRadius: 2 }}>
            {t('academy.quiz_questions.back_to_quizzes')}
          </Button>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openNew} disabled={!selectedQuizId} sx={{ borderRadius: 2 }}>
            {t('academy.quiz_questions.add_new')}
          </Button>
        </>
      }
    >
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 3, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('academy.quiz_questions.select_quiz')}
              select
              size="small"
              fullWidth
              sx={fsx}
              value={selectedQuizId || ''}
              onChange={e => handleQuizChange(Number(e.target.value))}
            >
              <MenuItem value="" disabled>{t('academy.quiz_questions.select_quiz_placeholder')}</MenuItem>
              {quizzes.map(q => (
                <MenuItem key={q.id} value={q.id}>{q.title} ({q.code})</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            {selectedQuiz && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={t('academy.quiz_questions.q_count', questions.length)} color="primary" variant="outlined" />
                <Chip label={t('academy.quiz_questions.total_points', questions.reduce((s, q) => s + (q.points || 1), 0))} color="success" variant="outlined" />
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      {!selectedQuizId ? (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 3, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}>
          <Typography variant="subtitle1" color="text.secondary">
            {t('academy.quiz_questions.no_quiz_selected')}
          </Typography>
        </Paper>
      ) : loading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('common.loading')}</Typography>
        </Box>
      ) : (
        <GenericTable
          columns={[
            { id: 'order', label: t('academy.quiz_questions.table.order'), minWidth: 70 },
            { id: 'text', label: t('academy.quiz_questions.table.text'), minWidth: 300 },
            {
              id: 'typeLabel',
              label: t('academy.quiz_questions.table.type'),
              minWidth: 110,
              format: (val: any) => <Chip label={typeLabel(val)} color="info" size="small" variant="outlined" />
            },
            {
              id: 'correctKeys',
              label: t('academy.quiz_questions.table.correct'),
              minWidth: 100,
              format: (val: any) => <Chip label={val} color="success" size="small" variant="outlined" />
            },
            { id: 'points', label: t('academy.quiz_questions.table.points'), minWidth: 90 },
          ]}
          data={rows}
          onEdit={(row) => openEdit(questions.find(q => q.id === row.id)!)}
          onDelete={(row) => setConfirmDelete(questions.find(q => q.id === row.id)!)}
          searchPlaceholder={t('academy.quiz_questions.search_placeholder')}
          maxHeight="calc(100vh - 420px)"
        />
      )}

      {/* Question edit dialog - standard RootDialogPage */}
      <RootDialogPage
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editingId ? t('academy.quiz_questions.dialog.edit') : t('academy.quiz_questions.dialog.new')}
        icon={editingId ? <EditIcon /> : <AddIcon />}
        onAction={handleSave}
        actionLabel={t('common.actions.save')}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={4}>
              <TextField label={t('academy.quiz_questions.form.type')} select size="small" fullWidth sx={fsx}
                value={formData.type ?? 'single'}
                onChange={e => handleTypeChange(e.target.value as QuizQuestionType)}>
                <MenuItem value="single">{t('academy.quiz_questions.form.type_single')}</MenuItem>
                <MenuItem value="multi">{t('academy.quiz_questions.form.type_multi')}</MenuItem>
                <MenuItem value="boolean">{t('academy.quiz_questions.form.type_boolean')}</MenuItem>
                <MenuItem value="text">{t('academy.quiz_questions.form.type_text')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField label={t('academy.quiz_questions.form.text')} size="small" fullWidth multiline rows={2} sx={fsx}
                value={formData.text ?? ''}
                onChange={e => setFormData(prev => ({ ...prev, text: e.target.value }))} />
            </Grid>

            {formData.type === 'text' ? (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  {t('academy.quiz_questions.form.text_hint')}
                </Alert>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('academy.quiz_questions.form.options')}
                  {formData.type === 'multi' && (
                    <Chip
                      label={t('academy.quiz_questions.form.multiple_correct')}
                      color="warning"
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(formData.options || []).map(option => (
                    <Box key={option.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={option.key} color="default" size="small" sx={{ minWidth: 42, justifyContent: 'center' }} />
                      <TextField
                        label={t('academy.quiz_questions.form.option_label')}
                        size="small"
                        fullWidth
                        sx={fsx}
                        value={option.label}
                        onChange={e => handleOptionChange(option.key, e.target.value)}
                      />
                      <FormControlLabel
                        label={t('academy.quiz_questions.form.correct')}
                        sx={{ mr: 0, '& .MuiTypography-root': { fontSize: '0.8rem' } }}
                        control={correctControl(option)}
                      />
                      {formData.type !== 'boolean' && (
                        <IconButton size="small" color="error" onClick={() => removeOption(option.key)} aria-label="remove option">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  {formData.type !== 'boolean' && (
                    <Button size="small" variant="text" startIcon={<AddIcon />} onClick={addOption} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
                      {t('academy.quiz_questions.form.add_option')}
                    </Button>
                  )}
                </Box>
              </Grid>
            )}

            <Grid item xs={6} sm={4}>
              <TextField label={t('academy.quiz_questions.form.order')} type="number" size="small" fullWidth sx={fsx}
                value={formData.order ?? 1}
                onChange={e => setFormData(prev => ({ ...prev, order: Number(e.target.value) }))} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField label={t('academy.quiz_questions.form.points')} type="number" size="small" fullWidth sx={fsx}
                value={formData.points ?? 1}
                onChange={e => setFormData(prev => ({ ...prev, points: Number(e.target.value) }))} />
            </Grid>
          </Grid>
        </Box>
      </RootDialogPage>

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle sx={{ fontWeight: 600 }}>{t('common.confirm.title.delete')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            {t('academy.quiz_questions.confirm_delete', confirmDelete?.text || '')}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(null)} variant="outlined" color="inherit">{t('btn.cancel')}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">{t('common.actions.confirm')}</Button>
        </DialogActions>
      </Dialog>
    </GenericPage>
  );
};

export default QuizQuestionsPage;
